import { session } from '@/auth/session';
import { BizError, HttpError } from './errors';
import type { Result } from './generated/types';

type Query = Record<string, unknown> | undefined;

/** Gateway origin: runtime config first (one image, many environments), then build-time env. */
const API_BASE: string = window.__VEO_CONFIG__?.apiBase || import.meta.env.VITE_API_BASE || '';

/** Fired on 401 so the app can drop the session and show the login screen. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

/** Serialize query values like Spring expects: arrays as repeated params, null/undefined omitted. */
export function toQueryString(query: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((item) => params.append(k, String(item)));
    else params.append(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  const type = res.headers.get('content-type') ?? '';
  if (type.includes('json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

async function send<T>(method: string, path: string, init: { query?: Query; body?: BodyInit; json?: boolean; headers?: Record<string, string | undefined>; signal?: AbortSignal }): Promise<T> {
  const headers = new Headers({ Accept: 'application/json' });
  for (const [key, value] of Object.entries(init.headers ?? {})) {
    if (value !== undefined) headers.set(key, value);
  }
  if (init.json) headers.set('Content-Type', 'application/json');
  const token = session.token();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(API_BASE + path + toQueryString(init.query), { method, headers, body: init.body, signal: init.signal });
  const body = await parse(res);
  if (res.status === 401) {
    onUnauthorized?.();
  }
  if (!res.ok) {
    const msg =
      (body && typeof body === 'object' && ('msg' in body ? (body as { msg: string }).msg : (body as { message?: string }).message)) ||
      `${res.status} ${res.statusText}`;
    throw new HttpError(res.status, String(msg), body);
  }
  return body as T;
}

export const http = {
  /** JSON request used by the generated endpoints. */
  request<T>(method: string, path: string, opts: { query?: Query; body?: unknown; headers?: Record<string, string | undefined>; signal?: AbortSignal }): Promise<T> {
    const hasBody = opts.body !== undefined;
    return send<T>(method, path, { query: opts.query, body: hasBody ? JSON.stringify(opts.body) : undefined, json: hasBody, headers: opts.headers, signal: opts.signal });
  },

  /** multipart/form-data request (file uploads). */
  multipart<T>(method: string, path: string, parts: Record<string, File | File[] | undefined>, query?: Query): Promise<T> {
    const form = new FormData();
    for (const [name, value] of Object.entries(parts)) {
      if (!value) continue;
      (Array.isArray(value) ? value : [value]).forEach((f) => form.append(name, f));
    }
    return send<T>(method, path, { query, body: form });
  },
};

/** Unwrap a Result envelope: returns data, or throws BizError when code !== 0. */
export function unwrap<T>(result: Result<T> | undefined): T {
  if (!result) throw new BizError(-1, 'Empty response', null);
  if (result.code !== 0) throw new BizError(result.code, result.msg, result.data);
  return result.data as T;
}
